import { useCallback, useEffect, useRef, useState } from 'react';
import {
  clampPan,
  clampScale,
  distance,
  doubleTapView,
  isDoubleTap,
  pinchScale,
  swipeIntent,
  type Point,
  type Tap,
} from './imageViewerGesture';

/**
 * Click-to-zoom for every image in the app.
 *
 * Images inside comments, descriptions and notes render at a capped height so
 * a tall screenshot cannot push a conversation off the screen. That cap is
 * right for reading a thread and wrong for actually looking at what somebody
 * pasted — a screenshot of a spreadsheet or a sample photo is unreadable at
 * 320px.
 *
 * This listens once at the document level rather than wiring a handler into
 * every place rich text renders. All of those paths already share the
 * .rich-text-view class, so one delegated listener covers comments, project
 * and task descriptions, supplier threads and anything added later — without
 * every future render site having to remember to opt in.
 *
 * On a phone it is also the Console's viewer (12 Oct): pinch, double tap, drag
 * to pan, swipe down to close, swipe sideways between the pictures of one
 * task. It is an overlay only. It never changes the route, reloads, or
 * remounts the page underneath it.
 */

/**
 * Where a click counts as "open this image".
 *
 * Rich text anywhere, plus anything explicitly marked. The opt-in class was
 * added for the public review portal, which renders proofs outside any rich
 * text and outside the app shell: a client inspecting artwork before approving
 * it is the case that most needs a full-screen look, and it was the one place
 * that could not have one.
 */
const ZOOMABLE = '.rich-text-view, .is-zoomable';

/**
 * An element carrying data-image-group is one set: every zoomable image inside
 * it can be paged through. An image outside any group opens alone.
 */
const GROUP = '[data-image-group]';

/**
 * A link that carries data-image-viewer is a plain-link fallback for when
 * scripts fail (the Console). With scripts running, the tap opens the viewer
 * and the link is not followed.
 */
const VIEWER_LINK = 'a[data-image-viewer]';

function isZoomable(target: EventTarget | null): target is HTMLImageElement {
  if (!(target instanceof HTMLImageElement) || target.closest(ZOOMABLE) === null) {
    return false;
  }
  // An image wrapped in a link is a link first — respect the author, unless
  // the link says it is only a fallback for the viewer.
  const link = target.closest('a');
  return link === null || link.matches(VIEWER_LINK);
}

/**
 * The zoomed copy is a NEW request for the same picture, and so is Download.
 * A posted GIPHY image carries referrerpolicy="no-referrer" (set by the
 * comment sanitizer, commentHtml.ts; a stored body cannot set it), so both
 * follow the clicked image: no-referrer when it had it, the browser default
 * otherwise. Nothing is widened: an image without the attribute behaves
 * exactly as before.
 */
interface OpenImage {
  src: string;
  alt: string;
  noReferrer: boolean;
}

interface OpenSet {
  items: OpenImage[];
  index: number;
}

interface View {
  scale: number;
  pan: Point;
}

const FITTED: View = { scale: 1, pan: { x: 0, y: 0 } };
/** A finger that moved less than this is a tap, not a drag. */
const TAP_SLOP = 10;

function describe(image: HTMLImageElement): OpenImage {
  return {
    src: image.currentSrc || image.src,
    alt: image.alt,
    noReferrer: image.getAttribute('referrerpolicy') === 'no-referrer',
  };
}

function collect(clicked: HTMLImageElement): OpenSet {
  const root = clicked.closest(GROUP);
  const found = root === null ? [] : Array.from(root.querySelectorAll('img')).filter(isZoomable);
  const index = found.indexOf(clicked);
  if (index < 0) {
    return { items: [describe(clicked)], index: 0 };
  }
  return { items: found.map(describe), index };
}

interface Gesture {
  mode: 'none' | 'drag' | 'pinch';
  start: Point;
  last: Point;
  startPan: Point;
  startScale: number;
  startDistance: number;
  moved: boolean;
  lastTap: Tap | null;
}

function newGesture(): Gesture {
  return {
    mode: 'none',
    start: { x: 0, y: 0 },
    last: { x: 0, y: 0 },
    startPan: { x: 0, y: 0 },
    startScale: 1,
    startDistance: 0,
    moved: false,
    lastTap: null,
  };
}

function point(touch: { clientX: number; clientY: number }): Point {
  return { x: touch.clientX, y: touch.clientY };
}

export function ImageLightbox() {
  const [set, setSet] = useState<OpenSet | null>(null);
  const [view, setView] = useState<View>(FITTED);
  const [busy, setBusy] = useState(false);
  const closeRef = useRef<HTMLButtonElement | null>(null);
  const stageRef = useRef<HTMLDivElement | null>(null);
  const returnFocusTo = useRef<Element | null>(null);
  const viewRef = useRef<View>(FITTED);
  const gesture = useRef<Gesture>(newGesture());
  // Back button: opening adds one history entry so Back closes the viewer
  // instead of leaving the page. Same URL, so the page underneath never
  // changes or remounts.
  const pushed = useRef(false);
  const ignorePop = useRef(false);

  const applyView = useCallback((next: View) => {
    viewRef.current = next;
    setView(next);
  }, []);

  useEffect(() => {
    function onClick(event: MouseEvent) {
      if (!isZoomable(event.target)) {
        return;
      }
      // Modified clicks belong to the browser, not to us.
      if (event.metaKey || event.ctrlKey || event.shiftKey || event.button !== 0) {
        return;
      }
      event.preventDefault();
      returnFocusTo.current = document.activeElement;
      gesture.current = newGesture();
      applyView(FITTED);
      setSet(collect(event.target));
      if (!pushed.current) {
        window.history.pushState(
          { ...(window.history.state as object | null), imageViewer: true },
          '',
        );
        pushed.current = true;
      }
    }
    document.addEventListener('click', onClick);
    return () => document.removeEventListener('click', onClick);
  }, [applyView]);

  const close = useCallback(() => {
    setSet(null);
    if (pushed.current) {
      pushed.current = false;
      ignorePop.current = true;
      window.history.back();
    }
    // Put the caret back where it was, so keyboard users are not dumped at
    // the top of the page after looking at a picture.
    if (returnFocusTo.current instanceof HTMLElement) {
      returnFocusTo.current.focus();
    }
  }, []);

  useEffect(() => {
    function onPop() {
      if (ignorePop.current) {
        ignorePop.current = false;
        return;
      }
      if (pushed.current) {
        // Back was pressed: the entry is already gone, so just hide the viewer.
        pushed.current = false;
        setSet(null);
        if (returnFocusTo.current instanceof HTMLElement) {
          returnFocusTo.current.focus();
        }
      }
    }
    window.addEventListener('popstate', onPop);
    return () => window.removeEventListener('popstate', onPop);
  }, []);

  const open = set !== null;
  const count = set?.items.length ?? 0;

  const go = useCallback(
    (step: number) => {
      setSet((current) => {
        if (current === null) {
          return current;
        }
        const index = current.index + step;
        return index < 0 || index >= current.items.length ? current : { ...current, index };
      });
      applyView(FITTED);
    },
    [applyView],
  );

  useEffect(() => {
    if (!open) {
      return;
    }
    closeRef.current?.focus();
    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    function onKey(event: KeyboardEvent) {
      if (event.key === 'Escape') {
        close();
      } else if (event.key === 'ArrowRight') {
        go(1);
      } else if (event.key === 'ArrowLeft') {
        go(-1);
      }
    }
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('keydown', onKey);
      document.body.style.overflow = previousOverflow;
    };
  }, [open, close, go]);

  const area = useCallback(() => {
    const box = stageRef.current?.getBoundingClientRect();
    return {
      width: box?.width ?? 0,
      height: box?.height ?? 0,
      left: box?.left ?? 0,
      top: box?.top ?? 0,
    };
  }, []);

  function onTouchStart(event: React.TouchEvent) {
    const g = gesture.current;
    const touches = event.touches;
    if (touches.length >= 2) {
      g.mode = 'pinch';
      g.moved = true;
      g.startScale = viewRef.current.scale;
      g.startDistance = distance(point(touches[0]!), point(touches[1]!));
    } else if (touches.length === 1) {
      g.mode = 'drag';
      g.moved = false;
      g.start = point(touches[0]!);
      g.last = g.start;
      g.startPan = viewRef.current.pan;
    }
  }

  function onTouchMove(event: React.TouchEvent) {
    const g = gesture.current;
    const touches = event.touches;
    const box = area();
    if (g.mode === 'pinch' && touches.length >= 2) {
      const scale = pinchScale(
        g.startScale,
        g.startDistance,
        distance(point(touches[0]!), point(touches[1]!)),
      );
      applyView({ scale, pan: clampPan(viewRef.current.pan, scale, box) });
    } else if (g.mode === 'drag' && touches.length === 1) {
      g.last = point(touches[0]!);
      const dx = g.last.x - g.start.x;
      const dy = g.last.y - g.start.y;
      if (Math.abs(dx) > TAP_SLOP || Math.abs(dy) > TAP_SLOP) {
        g.moved = true;
      }
      if (viewRef.current.scale > 1) {
        applyView({
          scale: viewRef.current.scale,
          pan: clampPan({ x: g.startPan.x + dx, y: g.startPan.y + dy }, viewRef.current.scale, box),
        });
      }
    }
  }

  function onTouchEnd(event: React.TouchEvent) {
    const g = gesture.current;
    if (event.touches.length > 0) {
      // One finger of a pinch lifted: carry on as a drag from here, no jump.
      g.mode = 'drag';
      g.moved = true;
      g.start = point(event.touches[0]!);
      g.last = g.start;
      g.startPan = viewRef.current.pan;
      return;
    }
    const wasDrag = g.mode === 'drag';
    g.mode = 'none';
    if (!wasDrag) {
      return;
    }
    const end = event.changedTouches[0] === undefined ? g.last : point(event.changedTouches[0]);
    if (!g.moved) {
      const tap: Tap = { time: Date.now(), x: end.x, y: end.y };
      if (isDoubleTap(g.lastTap, tap)) {
        g.lastTap = null;
        const box = area();
        applyView(
          doubleTapView(viewRef.current.scale, { x: tap.x - box.left, y: tap.y - box.top }, box),
        );
      } else {
        g.lastTap = tap;
      }
      return;
    }
    g.lastTap = null;
    const intent = swipeIntent(
      { dx: end.x - g.start.x, dy: end.y - g.start.y },
      viewRef.current.scale,
      count > 1,
    );
    if (intent === 'close') {
      close();
    } else if (intent === 'next') {
      go(1);
    } else if (intent === 'previous') {
      go(-1);
    }
  }

  /**
   * Drive serves these from another origin, so a plain download attribute is
   * ignored and the browser navigates instead. Fetching to a blob makes the
   * save work properly; when the fetch is refused, opening the original in a
   * new tab still gets the person to their file rather than failing silently.
   */
  const download = useCallback(async () => {
    const image = set?.items[set.index];
    if (image === undefined) {
      return;
    }
    setBusy(true);
    try {
      const response = await fetch(image.src, {
        mode: 'cors',
        ...(image.noReferrer ? { referrerPolicy: 'no-referrer' as const } : {}),
      });
      if (!response.ok) {
        throw new Error(String(response.status));
      }
      const url = URL.createObjectURL(await response.blob());
      const link = document.createElement('a');
      link.href = url;
      link.download = image.alt.trim() === '' ? 'image' : image.alt;
      link.click();
      URL.revokeObjectURL(url);
    } catch {
      window.open(image.src, '_blank', 'noopener,noreferrer');
    } finally {
      setBusy(false);
    }
  }, [set]);

  const image = set?.items[set.index];
  if (set === null || image === undefined) {
    return null;
  }

  return (
    <div
      className="lightbox"
      role="dialog"
      aria-modal="true"
      aria-label={image.alt.trim() === '' ? 'Image' : image.alt}
      onClick={(event) => {
        // Only a click on the backdrop itself closes; clicking the picture
        // you just opened should not dismiss it.
        if (event.target === event.currentTarget) {
          close();
        }
      }}
    >
      <div className="lightbox-bar">
        {count > 1 ? (
          <span className="lightbox-count" aria-live="polite">
            {`${String(set.index + 1)} of ${String(count)}`}
          </span>
        ) : null}
        <button type="button" className="lightbox-action" onClick={() => void download()}>
          {busy ? 'Downloading…' : 'Download'}
        </button>
        <button type="button" className="lightbox-action" ref={closeRef} onClick={close}>
          Close
        </button>
      </div>
      <div
        className="lightbox-stage"
        ref={stageRef}
        onTouchStart={onTouchStart}
        onTouchMove={onTouchMove}
        onTouchEnd={onTouchEnd}
        onTouchCancel={onTouchEnd}
        onClick={(event) => {
          if (event.target === event.currentTarget) {
            close();
          }
        }}
      >
        <img
          className="lightbox-image"
          src={image.src}
          alt={image.alt}
          referrerPolicy={image.noReferrer ? 'no-referrer' : undefined}
          draggable={false}
          style={{
            transform: `translate(${String(view.pan.x)}px, ${String(view.pan.y)}px) scale(${String(clampScale(view.scale))})`,
          }}
        />
        {count > 1 ? (
          <>
            <button
              type="button"
              className="lightbox-arrow lightbox-arrow--previous"
              aria-label="Previous image"
              disabled={set.index === 0}
              onClick={() => go(-1)}
            >
              ‹
            </button>
            <button
              type="button"
              className="lightbox-arrow lightbox-arrow--next"
              aria-label="Next image"
              disabled={set.index === count - 1}
              onClick={() => go(1)}
            >
              ›
            </button>
          </>
        ) : null}
      </div>
      <p className="lightbox-hint">Press Esc to close</p>
    </div>
  );
}
